import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  LuAlertCircle,
  LuArrowUp,
  LuBookOpen,
  LuFlag,
  LuHelpCircle,
  LuMessageSquarePlus,
  LuSparkles,
  LuUser,
  LuX,
} from "react-icons/lu";

import { useOtherWindowOpen } from "../../../hooks/useOtherWindowOpen";
import { useAiHelpChatMutation } from "../../../slices/AiSlice";
import { aiErrorMessage } from "../ai.utils";
import { useAiStatus } from "../useAiStatus";
import { Spinner } from "../WriteWithAi";
import {
  buildChatExcerpt,
  HelpMessage,
  loadMessages,
  newMessageId,
  redactPageUrl,
  safeLinks,
  saveMessages,
  SUGGESTED_QUESTIONS,
  trimHistory,
} from "./helpChat.utils";
import { HelpContactForm } from "./HelpContactForm";
import { HelpReplyText } from "./HelpReplyText";
import {
  HelpReportForm,
  primaryButton,
  secondaryButton,
} from "./HelpReportForm";

/*
 * Bottom-right "Help" button + "ProCharted help" panel on every signed-in
 * page (mounted in MainViewPages). Sits under the app's windows (z-40; the
 * windows use z-50 and up) and hides while a Headless UI window (z-10) is open.
 */

const TypingDots: FC = () => (
  <div
    role={"status"}
    aria-label={"The assistant is typing"}
    className={
      "inline-flex items-center gap-1 rounded-[14px] rounded-bl-[4px] bg-[#F3F4F7] px-3.5 py-3"
    }>
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className={"h-1.5 w-1.5 animate-bounce rounded-full bg-[#9AA0B1]"}
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </div>
);

type View = "chat" | "report" | "contact";

type PanelProps = {
  onClose: () => void;
  hidden: boolean;
};

const HelpPanel: FC<PanelProps> = ({ onClose, hidden }) => {
  const { isOn, isLoading: isStatusLoading, orgId } = useAiStatus();
  const [ask, { isLoading: isAsking }] = useAiHelpChatMutation();
  const [messages, setMessages] = useState<HelpMessage[]>(loadMessages);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>("chat");
  const [excerpt, setExcerpt] = useState<string | undefined>();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => saveMessages(messages), [messages]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // A new answer opens at its start (long answers used to open scrolled to
    // the end); anything else scrolls to the bottom.
    const last = messages[messages.length - 1];
    const answer =
      last?.role === "assistant" && !isAsking && !error && view === "chat"
        ? list.querySelector<HTMLElement>(`[data-message-id="${last.id}"]`)
        : null;
    list.scrollTop = answer
      ? answer.offsetTop - list.offsetTop - 8
      : list.scrollHeight;
  }, [messages, isAsking, error, view]);

  const sendHistory = async (history: HelpMessage[]) => {
    setError(null);
    try {
      const response = await ask({
        orgId,
        messages: trimHistory(history),
        page: redactPageUrl(window.location.href),
      }).unwrap();
      setMessages((current) => [
        ...current,
        {
          id: newMessageId(),
          role: "assistant",
          text: response.reply || "",
          links: safeLinks(response.links),
          suggestReport: !!response.suggestReport,
        },
      ]);
    } catch (caught) {
      setError(aiErrorMessage(caught));
    }
  };

  const sendText = (value: string) => {
    const text = value.trim();
    if (!text || isAsking) return;
    const next: HelpMessage[] = [
      ...messages,
      { id: newMessageId(), role: "user", text },
    ];
    setMessages(next);
    setDraft("");
    void sendHistory(next);
  };

  const openReport = (fromChat: boolean) => {
    setExcerpt(
      fromChat && messages.length ? buildChatExcerpt(messages) : undefined,
    );
    setView("report");
  };

  const openContact = () => {
    setExcerpt(messages.length ? buildChatExcerpt(messages) : undefined);
    setView("contact");
  };

  const newChat = () => {
    setMessages([]);
    setError(null);
    setDraft("");
    setView("chat");
    inputRef.current?.focus();
  };

  const renderChat = () => {
    if (isStatusLoading) {
      return (
        <div className={"flex flex-1 items-center justify-center"}>
          <Spinner />
        </div>
      );
    }
    if (!isOn) {
      return (
        <div
          className={
            "flex flex-1 flex-col items-center justify-center px-8 text-center"
          }>
          <div
            className={
              "flex h-12 w-12 items-center justify-center rounded-full bg-[#F3F4F7] text-[#8A90A2]"
            }>
            <LuHelpCircle size={24} strokeWidth={2} />
          </div>
          <div
            className={
              "mt-3 text-[13.5px] font-semibold leading-[1.5] text-[#3A4256]"
            }>
            The help assistant isn&apos;t available right now.
          </div>
          <div className={"mt-1 text-[12.5px] leading-[1.5] text-[#8A90A2]"}>
            Something not working? Let the ProCharted team know.
          </div>
          <button
            type={"button"}
            className={`${primaryButton} mt-5`}
            onClick={() => openReport(false)}>
            <LuFlag size={15} strokeWidth={2.2} />
            Report a problem
          </button>
          <button
            type={"button"}
            className={`${secondaryButton} mt-2`}
            onClick={openContact}>
            <LuUser size={15} strokeWidth={2.2} />
            Talk to a person
          </button>
        </div>
      );
    }
    return (
      <>
        <div
          ref={listRef}
          className={"min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4"}>
          <div className={"flex items-start gap-2.5"}>
            <div
              className={
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EEEAFF] text-[#6B4EFF]"
              }>
              <LuSparkles size={15} strokeWidth={2.2} />
            </div>
            <div
              className={
                "rounded-[14px] rounded-tl-[4px] bg-[#F3F4F7] px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-[#1F2433]"
              }>
              Hi! Ask me how to do something in ProCharted, or report a problem.
            </div>
          </div>
          <div className={"pl-[38px]"}>
            <button
              type={"button"}
              onClick={openContact}
              className={
                "inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-[#DCE2EA] bg-white px-3 py-1 text-[12px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
              }>
              <LuUser size={13} strokeWidth={2.2} />
              Talk to a person
            </button>
          </div>
          {!messages.length && (
            <div className={"flex flex-wrap gap-2 pl-[38px]"}>
              {SUGGESTED_QUESTIONS.map((question) => (
                <button
                  key={question}
                  type={"button"}
                  onClick={() => sendText(question)}
                  className={
                    "rounded-full border-[1.5px] border-[#DCD5FF] bg-white px-3 py-1.5 text-left text-[12.5px] font-semibold text-[#5A3FE6] hover:border-[#6B4EFF] hover:bg-[#F7F5FF]"
                  }>
                  {question}
                </button>
              ))}
            </div>
          )}
          {messages.map((message) =>
            message.role === "user" ? (
              <div key={message.id} className={"flex justify-end"}>
                <div
                  className={
                    "max-w-[85%] whitespace-pre-wrap break-words rounded-[14px] rounded-br-[4px] bg-[#6B4EFF] px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-white"
                  }>
                  {message.text}
                </div>
              </div>
            ) : (
              <div
                key={message.id}
                data-message-id={message.id}
                className={"max-w-[92%]"}>
                <div
                  className={
                    "break-words rounded-[14px] rounded-bl-[4px] bg-[#F3F4F7] px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-[#2A3042]"
                  }>
                  <HelpReplyText text={message.text} />
                </div>
                <div className={"mt-2 flex flex-wrap gap-1.5"}>
                  {message.links?.map((link) => (
                    <a
                      key={link.url}
                      href={link.url}
                      target={"_blank"}
                      rel={"noopener noreferrer"}
                      title={link.title}
                      className={
                        "inline-flex max-w-full items-center gap-1.5 rounded-full border-[1.5px] border-[#DCE2EA] bg-white px-3 py-1 text-[12px] font-bold text-[#1F2433] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
                      }>
                      <LuBookOpen size={13} strokeWidth={2.2} />
                      <span className={"truncate"}>
                        {link.title || "Read the article"}
                      </span>
                    </a>
                  ))}
                  {message.suggestReport && (
                    <button
                      type={"button"}
                      onClick={() => openReport(true)}
                      className={
                        "inline-flex items-center gap-1.5 rounded-full bg-[#6B4EFF] px-3 py-1 text-[12px] font-bold text-white hover:bg-[#5A3FE6]"
                      }>
                      <LuFlag size={13} strokeWidth={2.2} />
                      Report a problem
                    </button>
                  )}
                  <button
                    type={"button"}
                    onClick={openContact}
                    className={
                      "inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-[#DCE2EA] bg-white px-3 py-1 text-[12px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
                    }>
                    <LuUser size={13} strokeWidth={2.2} />
                    Talk to a person
                  </button>
                </div>
              </div>
            ),
          )}
          {isAsking && <TypingDots />}
          {error && (
            <div
              role={"alert"}
              className={
                "flex items-start gap-2 rounded-[12px] border border-[#F6C9CB] bg-[#FDF0F0] px-3 py-2.5 text-[12.5px] leading-[1.45] text-[#C8333A]"
              }>
              <LuAlertCircle
                size={15}
                strokeWidth={2.2}
                className={"mt-[1px] shrink-0"}
              />
              <div className={"min-w-0 flex-1"}>
                <div className={"font-semibold"}>{error}</div>
                <button
                  type={"button"}
                  className={"mt-1 font-bold underline"}
                  onClick={() => void sendHistory(messages)}>
                  Try again
                </button>
              </div>
            </div>
          )}
        </div>
        <div className={"border-t border-[#EDEFF3] px-4 pb-3 pt-3"}>
          <div
            className={
              "flex items-end gap-2 rounded-[12px] border-[1.5px] border-[#DCE2EA] bg-white pl-3 focus-within:border-[#6B4EFF]"
            }>
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              aria-label={"Ask a question"}
              placeholder={"Ask a question…"}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  sendText(draft);
                }
              }}
              className={
                "max-h-[96px] min-h-[40px] min-w-0 flex-1 resize-none bg-transparent py-2.5 text-[13.5px] leading-[1.4] text-[#1F2433] outline-none placeholder:text-[#A3A9B8]"
              }
              style={{
                height: `${Math.min(96, 20 + 19 * draft.split("\n").length)}px`,
              }}
            />
            <button
              type={"button"}
              title={"Send"}
              aria-label={"Send"}
              disabled={!draft.trim() || isAsking}
              onClick={() => sendText(draft)}
              className={
                "m-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#6B4EFF] text-white disabled:opacity-40"
              }>
              <LuArrowUp size={17} strokeWidth={2.4} />
            </button>
          </div>
          <div className={"mt-1.5 flex items-start gap-3 px-1"}>
            <div
              className={
                "min-w-0 flex-1 text-[11px] leading-[1.4] text-[#8A90A2]"
              }>
              AI answers can be wrong. Check the help center if unsure.
            </div>
            <button
              type={"button"}
              onClick={() => openReport(true)}
              className={
                "inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11.5px] font-bold text-[#6B4EFF] hover:underline"
              }>
              <LuFlag size={12} strokeWidth={2.3} />
              Report a problem
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      role={"dialog"}
      aria-modal={"false"}
      aria-label={"ProCharted help"}
      data-help-panel={""}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
      className={classNames(
        "fixed bottom-[72px] right-4 z-40 flex h-[560px] max-h-[calc(100vh-110px)] w-[380px] flex-col overflow-hidden rounded-[16px] border border-[#EDEFF3] bg-white font-Manrope shadow-[0_18px_48px_rgba(31,36,51,0.22)]",
        // Phones: full width from the bottom (the round button is hidden there).
        // Desktop values are the defaults: an "sm:bottom" override lost to
        // another bottom-0 rule on staging and the round button covered Send.
        "max-sm:inset-x-0 max-sm:bottom-0 max-sm:right-auto max-sm:h-[85vh] max-sm:max-h-none max-sm:w-auto max-sm:rounded-b-none",
      )}
      // Inline: the "hidden" class lost to "flex" on staging, so the panel
      // could not be closed (Roadmap 1832).
      style={hidden ? { display: "none" } : undefined}>
      <div
        className={
          "flex items-center gap-2 border-b border-[#EDEFF3] py-3 pl-4 pr-2"
        }>
        <div
          className={
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#6B4EFF] text-white"
          }>
          <LuSparkles size={16} strokeWidth={2.2} />
        </div>
        <div
          className={
            "min-w-0 flex-1 truncate text-[15px] font-extrabold text-[#1F2433]"
          }>
          ProCharted help
        </div>
        {isOn && (
          <button
            type={"button"}
            title={"New chat"}
            aria-label={"New chat"}
            disabled={!messages.length && view === "chat"}
            onClick={newChat}
            className={
              "inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7185] hover:bg-[#F1F2F5] disabled:opacity-40"
            }>
            <LuMessageSquarePlus size={17} strokeWidth={2} />
          </button>
        )}
        <button
          type={"button"}
          title={"Close"}
          aria-label={"Close help"}
          onClick={onClose}
          className={
            "inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7185] hover:bg-[#F1F2F5]"
          }>
          <LuX size={18} strokeWidth={2.2} />
        </button>
      </div>
      {view === "contact" ? (
        <HelpContactForm
          orgId={orgId}
          chatExcerpt={excerpt}
          onCancel={() => setView("chat")}
          onDone={() => setView("chat")}
        />
      ) : view === "report" ? (
        <HelpReportForm
          orgId={orgId}
          chatExcerpt={excerpt}
          onCancel={() => setView("chat")}
          onDone={() => setView("chat")}
        />
      ) : (
        renderChat()
      )}
    </div>
  );
};

/** The round "Help" button and its panel (signed-in app pages only). */
export const HelpChat: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  // Stays mounted after the first open so an answer on its way isn't lost.
  const [hasOpened, setHasOpened] = useState(false);
  const otherWindowOpen = useOtherWindowOpen();

  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) setHasOpened(true);
  }, [isOpen]);

  // A click anywhere outside the panel (and its round button) closes it.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || !document.contains(target)) return;
      const panel = document.querySelector("[data-help-panel]");
      if (panel?.contains(target) || buttonRef.current?.contains(target))
        return;
      setIsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isOpen]);

  return createPortal(
    <>
      {(isOpen || hasOpened) && (
        <HelpPanel
          onClose={() => setIsOpen(false)}
          hidden={!isOpen || otherWindowOpen}
        />
      )}
      <div
        ref={buttonRef}
        className={classNames(
          "group fixed bottom-4 right-4 z-40 font-Manrope",
          (otherWindowOpen || isOpen) && "max-sm:hidden",
        )}
        style={otherWindowOpen ? { display: "none" } : undefined}>
        <button
          type={"button"}
          aria-label={isOpen ? "Close help" : "Help"}
          data-help-chat-button={true}
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
          className={
            "flex h-11 w-11 items-center justify-center rounded-full bg-[#6B4EFF] text-white shadow-[0_6px_18px_rgba(107,78,255,0.38)] transition-transform hover:scale-105 hover:bg-[#5A3FE6]"
          }>
          {isOpen ? (
            <LuX size={22} strokeWidth={2.3} />
          ) : (
            <LuHelpCircle size={23} strokeWidth={2.1} />
          )}
        </button>
        {!isOpen && (
          <span
            role={"tooltip"}
            className={
              "pointer-events-none absolute bottom-[52px] right-0 whitespace-nowrap rounded-md bg-[#1F2433] px-2.5 py-1 text-[12px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100"
            }>
            Help
          </span>
        )}
      </div>
    </>,
    document.body,
  );
};
